import { useEffect, useState } from "react";
import Papa from "papaparse";
import {
  getAllForms,
  deleteForm,
  doneForm,
  verifyDownPayment,
  verifyFullPayment,
} from "../../services/formService";
import "./SubmittedForms.css";
import Loading from "../loading/Loading";

const SubmittedForms = () => {
  const [forms, setForms] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showToday, setShowToday] = useState(false);

  useEffect(() => {
    fetchForms(page);
    const interval = setInterval(() => {
      fetchForms(page);
    }, 5000);
    return () => clearInterval(interval);
  }, [page]);

  const fetchForms = async (currentPage) => {
    try {
      const data = await getAllForms(currentPage);
      setForms(data?.forms || []);
      setTotalPages(data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching forms:", error);
      setForms([]);
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this booking? This cannot be undone."
      )
    ) {
      await deleteForm(id);
      setForms((prevForms) => prevForms.filter((form) => form._id !== id));
      setLoading(true);
      fetchForms(page);
      setLoading(false);
    }
  };

  const handleDone = async (id) => {
    if (
      window.confirm(
        "Move this booking to Done Bookings? This cannot be undone."
      )
    ) {
      setLoading(true);
      await doneForm(id);
      fetchForms(page);
      setLoading(false);
    }
  };

  const handleVerifyDownPayment = async (id) => {
    const status = prompt(
      "Enter Down Payment status (e.g., Amount, Verified, Pending, Failed):"
    );
    if (status) {
      setLoading(true);
      await verifyDownPayment(id, status);
      fetchForms(page);
      setLoading(false);
    }
  };

  const handleVerifyFullPayment = async (id) => {
    const status = prompt(
      "Enter Full Payment status (e.g., Verified, Pending, Failed):"
    );
    if (status) {
      setLoading(true);
      await verifyFullPayment(id, status);
      fetchForms(page);
      setLoading(false);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : prevOrder === "desc" ? null : "asc"
    );
  };

  const sortedForms = [...forms];
  if (sortOrder) {
    sortedForms.sort((a, b) => {
      return sortOrder === "asc"
        ? new Date(a.startDate) - new Date(b.startDate)
        : new Date(b.startDate) - new Date(a.startDate);
    });
  }

  const filterFormsForToday = (forms) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today's date to start of the day

    return forms.filter((form) => {
      const startDate = new Date(form.startDate);
      const endDate = new Date(form.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return today >= startDate && today <= endDate;
    });
  };

  const filteredForms = showToday
    ? filterFormsForToday(sortedForms)
    : sortedForms;

  const exportToCSV = () => {
    const csvData = forms.map((form) => ({
      Name: form.name,
      Contact: form.contactNumber,
      "Start Date": new Date(form.startDate).toLocaleDateString("en-US"),
      "End Date": new Date(form.endDate).toLocaleDateString("en-US"),
      Guests: form.guestCount,
      Message: form.message,
      "Total Price": form.totalPrice,
      "Down Payment Status": form.downPaymentVerified,
      "Full Payment Status": form.fullPaymentVerified,
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bookings.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="table-container">
      {loading && <Loading />}

      <h2>All Bookings</h2>
      <div className="sort-buttons">
        <button className="sort-button" onClick={toggleSortOrder}>
          Sort by Start Date (
          {sortOrder === "asc"
            ? "Ascending"
            : sortOrder === "desc"
            ? "Descending"
            : "Default"}
          )
        </button>
        <button
          className="today-button"
          onClick={() => setShowToday(!showToday)}
        >
          {showToday ? "Show All" : "Show Today"}
        </button>
        <button className="export-button" onClick={exportToCSV}>
          Export to CSV
        </button>
      </div>

      {filteredForms.length === 0 ? (
        <div className="no-bookings-message">No available bookings.</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Guests</th>
              <th>Message</th>
              <th>Tour Availed</th>
              <th>Total Price</th>
              <th>Actions</th>
              <th>Downpayment</th>
              <th>FullPayment</th>
            </tr>
          </thead>
          <tbody>
            {filteredForms.map((form) => (
              <tr key={form._id}>
                <td>{form.name}</td>
                <td>{form.contactNumber}</td>
                <td>
                  {new Date(form.startDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </td>
                <td>
                  {new Date(form.endDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </td>
                <td>{form.guestCount}</td>
                <td>
                  <p className="messageDash">{form.message}</p>
                </td>
                <td>
                  <ul className="room-list">
                    {form.cart.map((item) => (
                      <li key={item._id}>
                        <img
                          src={item.imgUrl}
                          alt={item.name}
                          className="room-img"
                        />
                        <span>
                          {item.name} (x{item.quantity})
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>
                <td>₱{form.totalPrice.toLocaleString()}</td>
                <td className="actionsAdmin">
                  <button
                    className="done-button"
                    onClick={() => handleDone(form._id)}
                  >
                    Mark as Done
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(form._id)}
                  >
                    Delete
                  </button>
                </td>
                <td
                  className={`status ${form.downPaymentVerified.toLowerCase()} `}
                >
                  <button
                    className="downpayment-button"
                    onClick={() => handleVerifyDownPayment(form._id)}
                  >
                    DownPayment
                  </button>
                  {form.downPaymentVerified}
                </td>
                <td
                  className={`status ${form.fullPaymentVerified.toLowerCase()}`}
                >
                  <button
                    className="fullpayment-button"
                    onClick={() => handleVerifyFullPayment(form._id)}
                  >
                    Full Payment
                  </button>
                  {form.fullPaymentVerified}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination Controls */}
      <div className="submittedPagination">
        <button
          onClick={() => {
            setPage((prev) => Math.max(prev - 1, 1));
            window.scrollTo(0, 0);
          }}
          disabled={page === 1}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => {
            setPage((prev) => (prev < totalPages ? prev + 1 : prev));
            window.scrollTo(0, 0);
          }}
          disabled={page === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SubmittedForms;
